import { clinicDateTime, parseCalendarInstant } from '../../../utils/calendarTime';
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Autocomplete,
  Box,
  Typography,
  Grid,
  Divider,
  Link,
  Chip
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider, DateTimePicker } from '@mui/x-date-pickers';
import appointmentsApi from '../../../api/appointments';
import { useQuery } from '@tanstack/react-query';
import { useAppTheme } from '../../../contexts/ThemeContext';
import { getTenantCacheKey } from '../../../utils/auth';
import { useCurrency } from '../../../hooks/useCurrency';
import { getCurrencySymbol, formatCurrency } from '../../../utils/currency';
import { formatClientName } from '../../../utils/displayNames';

// Import API client functions
import clientsApi from '../../../api/clientsApi';
import { useServiceCategories, useServicesByCategory } from '../../../hooks/useServices';
import { isInternalPlanUser } from '../../../utils/featureAccess';

const STATUS_OPTIONS = [
  { value: 'scheduled', label: 'Scheduled' },
  { value: 'confirmed', label: 'Confirmed' },
  { value: 'arrived', label: 'Arrived' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'completed', label: 'Completed' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'no_show', label: 'No Show' }
];

const STATUS_FALLBACK = 'scheduled';

const STATUS_NORMALIZATION_MAP = {
  waiting: STATUS_FALLBACK,
  waitlist: STATUS_FALLBACK,
  hold: STATUS_FALLBACK,
  complete: 'completed',
  completed: 'completed',
  'in-progress': 'in_progress',
  inprogress: 'in_progress',
  'no-show': 'no_show',
  noshow: 'no_show'
};

const normalizeStatus = (status) => {
  if (!status) {
    return STATUS_FALLBACK;
  }

  const prepared = status
    .toString()
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '_');

  const normalized = STATUS_NORMALIZATION_MAP[prepared] || prepared.replace(/-/g, '_');

  return STATUS_OPTIONS.some(option => option.value === normalized) ? normalized : STATUS_FALLBACK;
};

const getStatusLabel = (value) => STATUS_OPTIONS.find(option => option.value === value)?.label || value;

const dialogPaperSx = {
  width: { xs: '100%', sm: 720, md: 960 },
  maxWidth: '92vw'
};

const halfWidthItemSx = {
  width: '100%',
  minWidth: { xs: '100%', md: 320 }
};

const AppointmentFormDialog = ({ open, onClose, date, appointment, onSave, timeZone }) => {
  // Navigation hook
  const navigate = useNavigate();

  // Get current theme
  const { currentTheme = {} } = useAppTheme() || {};

  // Get current currency
  const { currency } = useCurrency();
  const currencySymbol = getCurrencySymbol(currency);

  // Check if user can edit appointments
  const canEdit = isInternalPlanUser();

  // Create initial form data function
  const getInitialFormData = () => ({
    client_id: '',
    user_id: '',
    location_id: '',
    start_date: new Date(),
    end_date: new Date(new Date().getTime() + 30 * 60000),
    notes: '',
    status: STATUS_FALLBACK,
    services: []
  });

  const [formData, setFormData] = useState(getInitialFormData());
  const [selectedCategory, setSelectedCategory] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Debugging - Log appointment data when it changes
  useEffect(() => {
    if (appointment) {
      console.log("Appointment data in form:", appointment);
    }
  }, [appointment]);
  
  // Fetch complete appointment data when editing
  const { data: fullAppointmentData } = useQuery({
    queryKey: [getTenantCacheKey('appointment'), appointment?.id],
    queryFn: () => appointmentsApi.getAppointment(appointment.id),
    enabled: open && !!appointment?.id,
    staleTime: 5 * 60 * 1000,
  });

  // Use the full appointment data if available, otherwise fall back to the passed appointment
  const appointmentToUse = fullAppointmentData || appointment;
  const isReadOnly = appointmentToUse && !canEdit;
  
  // Fetch clients for dropdown
  const { data: clientsData, isLoading: clientsLoading, error: clientsError } = useQuery({
    queryKey: [getTenantCacheKey('clients')],
    queryFn: () => clientsApi.getClients(),
    staleTime: 5 * 60 * 1000,
    enabled: open && !appointmentToUse // Only run when dialog is open and creating new appointment
  });
  
  const clients = clientsData?.clients || [];
  
  // Debug clients data
  useEffect(() => {
    if (open && !appointmentToUse) {
      console.log("Clients loading:", clientsLoading);
      console.log("Clients error:", clientsError);
      console.log("Clients data:", clientsData);
      console.log("Clients array:", clients);
      console.log("Number of clients:", clients.length);
      if (clients.length > 0) {
        console.log("First client:", clients[0]);
      }
    }
  }, [open, clientsLoading, clientsError, clientsData, clients, appointmentToUse]);

  // Fetch providers
  const { data: providers = [] } = useQuery({
    queryKey: [getTenantCacheKey('providers')],
    queryFn: () => appointmentsApi.getProviders(),
    staleTime: 5 * 60 * 1000,
    enabled: open && !appointmentToUse
  });

  // Fetch locations
  const { data: locations = [] } = useQuery({
    queryKey: [getTenantCacheKey('locations')],
    queryFn: () => appointmentsApi.getLocations(),
    staleTime: 5 * 60 * 1000,
    enabled: open && !appointmentToUse
  });

  // Category-driven service loading (matches Services pages)
  const [selectedCategoryId, setSelectedCategoryId] = useState(null);
  const { data: categoriesData, isLoading: categoriesLoading, error: categoriesError } = useServiceCategories();
  const categories = categoriesData?.categories || [];
  const {
    data: servicesByCategoryData,
    isLoading: servicesLoading,
    error: servicesError
  } = useServicesByCategory(selectedCategoryId, { page: 1, limit: 100 });
  const services = servicesByCategoryData?.services || [];

  // Debug services data
  useEffect(() => {
    if (open) {
      console.log("Services data in form:", services);
      console.log("Services length:", services?.length);
      console.log("First service:", services?.[0]);
    }
  }, [services, open]);
  
  // Debug appointment data
  useEffect(() => {
    if (appointmentToUse) {
      console.log("Appointment data structure:", appointmentToUse);
      console.log("Appointment services:", appointmentToUse.services);
      console.log("First appointment service:", appointmentToUse.services?.[0]);
    }
  }, [appointmentToUse]);
  
  // Reset form when appointment changes
  useEffect(() => {
    if (open) {
      // Always reset selectedCategory when opening dialog
      setSelectedCategory('');
      setError(null);

      if (appointmentToUse) {
        setFormData({
          client_id: appointmentToUse.client_id || '',
          user_id: appointmentToUse.user_id || '',
          location_id: appointmentToUse.location_id || '',
          start_date: (timeZone && isReadOnly ? parseCalendarInstant(appointmentToUse.start_date) : new Date(appointmentToUse.start_date)),
          end_date: (timeZone && isReadOnly ? parseCalendarInstant(appointmentToUse.end_date) : new Date(appointmentToUse.end_date)),
          notes: appointmentToUse.notes || '',
          status: normalizeStatus(appointmentToUse.status),
          services: appointmentToUse.services || []
        });

        // If appointment has services, try to set the category from the first service
        if (appointmentToUse.services && appointmentToUse.services.length > 0) {
          const firstService = appointmentToUse.services[0];
          if (firstService.category_id) {
            setSelectedCategory(firstService.category_id);
          }
        }
      } else if (date) {
        // If it's a new appointment with a selected date
        const startDate = date.start || date;
        const endDate = date.end || new Date(new Date(startDate).getTime() + 30 * 60000);

        setFormData({
          ...getInitialFormData(),
          start_date: new Date(startDate),
          end_date: new Date(endDate)
        });
      } else {
        // Reset form for new appointments
        setFormData(getInitialFormData());
      }
    } else {
      // When dialog closes, reset everything
      setFormData(getInitialFormData());
      setSelectedCategory('');
      setError(null);
    }
  }, [appointmentToUse, date, open, timeZone, isReadOnly]);
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    let nextValue = value;

    if (name === 'status') {
      nextValue = normalizeStatus(value);
    } else if (['user_id', 'location_id'].includes(name)) {
      nextValue = value ?? '';
    }

    setFormData(prev => ({ ...prev, [name]: nextValue }));
  };
  
  const handleClientChange = (_, newValue) => {
    setFormData(prev => ({ 
      ...prev, 
      client_id: newValue ? newValue.id : '' 
    }));
  };

  const handleProviderChange = (_, newValue) => {
    setFormData(prev => ({ 
      ...prev, 
      user_id: newValue ? newValue.id : '' 
    }));
  };

  const handleLocationChange = (_, newValue) => {
    setFormData(prev => ({ 
      ...prev, 
      location_id: newValue ? newValue.id : '' 
    }));
  };

  const handleStartDateChange = (newValue) => {
    if (!newValue) {
      setFormData(prev => ({ ...prev, start_date: null, end_date: null }));
      return;
    }

    const startDate = new Date(newValue);
    const defaultEnd = new Date(startDate.getTime() + 30 * 60000);

    setFormData(prev => ({
      ...prev,
      start_date: startDate,
      end_date: defaultEnd
    }));
  };

  const handleEndDateChange = (newValue) => {
    if (!newValue) {
      setFormData(prev => ({ ...prev, end_date: null }));
      return;
    }

    setFormData(prev => ({
      ...prev,
      end_date: new Date(newValue)
    }));
  };

  // Memoized service value calculation to prevent infinite loops
  const selectedServiceValue = useMemo(() => {
    const currentService = formData.services?.[0];
    
    if (!currentService) {
      return '';
    }

    // Try to match by service_id first
    if (currentService.service_id) {
      const matchedService = services?.find(s => s.id === currentService.service_id);
      if (matchedService) {
        return currentService.service_id;
      }
    }
    
    // Try to match by id
    if (currentService.id) {
      const matchedService = services?.find(s => s.id === currentService.id);
      if (matchedService) {
        return currentService.id;
      }
    }
    
    // Try to match by service_name
    if (currentService.service_name) {
      const matchedService = services?.find(s => s.service_name === currentService.service_name);
      if (matchedService) {
        return matchedService.id;
      }
    }
    
    return '';
  }, [formData.services, services]);
  
  const handleSubmit = async () => {
    try {
      setLoading(true);
      setError(null);
      
      if (!isInternalPlanUser()) {
        setError('Write operations are limited to internal plan during beta.');
        return;
      }

      if (appointmentToUse) {
        // Update existing appointment
        await appointmentsApi.updateAppointment(appointmentToUse.id, formData);
      } else {
        // Create new appointment
        await appointmentsApi.createAppointment(formData);
      }
      
      onSave();
      onClose();
    } catch (err) {
      console.error('Error saving appointment:', err);
      setError(err.message || 'Failed to save appointment');
    } finally {
      setLoading(false);
    }
  };

  // Find the client object matching the selected ID
  const selectedClient = clients?.find(client => client.id === formData.client_id);
  const selectedProvider = providers?.find(provider => provider.id === formData.user_id);
  const selectedLocation = locations?.find(location => location.id === formData.location_id);
  
  // Status options with proper values from database
  const statusOptions = STATUS_OPTIONS;

  const statusValue = formData.status || STATUS_FALLBACK;

  const formatDateTime = (value) => {
    if (!value) {
      return '';
    }

    if (timeZone && isReadOnly) return clinicDateTime(value instanceof Date ? value : parseCalendarInstant(value), timeZone).setLocale('en-GB').toFormat('dd LLL yyyy HH:mm');
    return value instanceof Date ? value.toLocaleString() : new Date(value).toLocaleString();
  };

  const focusColor = currentTheme?.primary || null;

  const commonTextFieldSx = useMemo(() => ({
    '& .MuiOutlinedInput-root': {
      borderRadius: 2,
      backgroundColor: 'background.paper',
      transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
      '& fieldset': {
        borderColor: 'divider'
      },
      '&:hover fieldset': {
        borderColor: focusColor || 'primary.main'
      },
      '&.Mui-focused fieldset': {
        borderColor: focusColor || 'primary.main',
        boxShadow: (theme) => `0 0 0 2px ${alpha(focusColor || theme.palette.primary.main, 0.18)}`
      }
    }
  }), [focusColor]);

  const ReadOnlyField = ({ label, children, alignTop = false, minHeight = 48 }) => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
      <Typography
        variant="caption"
        sx={{
          color: 'text.secondary',
          fontWeight: 600,
          mb: 0.5,
          lineHeight: 1.2
        }}
      >
        {label}
      </Typography>
      <Box
        sx={{
          borderRadius: 2,
          bgcolor: 'action.hover',
          border: '1px solid',
          borderColor: 'divider',
          px: 2,
          py: 1.25,
          minHeight,
          display: 'flex',
          alignItems: alignTop ? 'flex-start' : 'center',
          justifyContent: 'flex-start',
          gap: 1,
          flexWrap: 'wrap',
          alignContent: 'flex-start',
          width: '100%',
          wordBreak: 'break-word'
        }}
      >
        {children}
      </Box>
    </Box>
  );

  const handleClientClick = (e) => {
    if (appointmentToUse?.client_id) {
      e.preventDefault();
      onClose(); // Close the dialog first
      navigate(`/clients/${appointmentToUse.client_id}`);
    }
  };

  const renderClientField = () => {
    if (isReadOnly && appointmentToUse) {
      return (
        <Box>
          <Typography
            variant="caption"
            sx={{
              color: 'text.secondary',
              display: 'block',
              fontWeight: 600,
              mb: 0.5,
              lineHeight: 1.2
            }}
          >
            Client
          </Typography>
          <Link
            component="button"
            onClick={handleClientClick}
            sx={{
              textDecoration: 'none',
              color: currentTheme?.primary || 'primary.main',
              fontWeight: 500,
              fontSize: '1rem',
              textAlign: 'left',
              cursor: 'pointer',
              '&:hover': {
                textDecoration: 'underline'
              }
            }}
          >
            {formatClientName(appointmentToUse)}
          </Link>
        </Box>
      );
    }

    return (
      <Autocomplete
        id="client-select"
        options={clients || []}
        loading={clientsLoading}
        loadingText="Loading clients..."
        noOptionsText={clientsError ? 'Error loading clients' : 'No clients found'}
        getOptionLabel={(option) => formatClientName(option)}
        value={selectedClient || null}
        onChange={handleClientChange}
        isOptionEqualToValue={(option, value) => option?.id === value?.id}
        getOptionKey={(option) => option.id || Math.random().toString()}
        sx={{ width: '100%' }}
        renderOption={(props, option) => (
          <Box component="li" {...props} key={option.id}>
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <Typography variant="body2">{formatClientName(option)}</Typography>
              {option.email && (
                <Typography variant="caption" color="text.secondary">
                  {option.email}
                </Typography>
              )}
            </Box>
          </Box>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Client"
            required
            fullWidth
            error={!formData.client_id}
            helperText={!formData.client_id ? 'Client is required' : ''}
            size="small"
            sx={{
              ...commonTextFieldSx,
              '& .MuiFormHelperText-root': {
                ml: 0
              }
            }}
          />
        )}
      />
    );
  };

  const renderStatusField = () => {
    if (isReadOnly) {
      return (
        <ReadOnlyField label="Status">
          <Typography variant="body1" sx={{ color: 'text.primary', fontWeight: 500 }}>
            {getStatusLabel(statusValue)}
          </Typography>
        </ReadOnlyField>
      );
    }

    return (
      <FormControl fullWidth size="small" sx={commonTextFieldSx}>
        <InputLabel id="status-label">Status</InputLabel>
        <Select
          labelId="status-label"
          name="status"
          value={statusValue}
          onChange={handleChange}
          label="Status"
        >
          {statusOptions.map(option => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    );
  };

  const renderProviderField = () => {
    if (isReadOnly && appointmentToUse) {
      return (
        <ReadOnlyField label="Provider">
          <Typography variant="body1" sx={{ color: 'text.primary' }}>
            {appointmentToUse.user_name || 'Unknown Provider'}
          </Typography>
        </ReadOnlyField>
      );
    }

    return (
      <Autocomplete
        id="provider-select"
        options={providers || []}
        getOptionLabel={(option) => option.full_name || option.email || ''}
        value={selectedProvider || null}
        onChange={handleProviderChange}
        isOptionEqualToValue={(option, value) => option?.id === value?.id}
        sx={{ width: '100%' }}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Provider"
            fullWidth
            size="small"
            sx={commonTextFieldSx}
          />
        )}
      />
    );
  };

  const renderLocationField = () => {
    if (isReadOnly && appointmentToUse) {
      return (
        <ReadOnlyField label="Location">
          <Typography variant="body1" sx={{ color: 'text.primary' }}>
            {appointmentToUse.location_name || 'Unknown Location'}
          </Typography>
        </ReadOnlyField>
      );
    }

    return (
      <Autocomplete
        id="location-select"
        options={locations || []}
        getOptionLabel={(option) => option.location_name || ''}
        value={selectedLocation || null}
        onChange={handleLocationChange}
        isOptionEqualToValue={(option, value) => option?.id === value?.id}
        sx={{ width: '100%' }}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Location"
            fullWidth
            size="small"
            sx={commonTextFieldSx}
          />
        )}
      />
    );
  };

  const renderStartField = () => {
    if (isReadOnly) {
      return (
        <ReadOnlyField label="Start Date & Time">
          <Typography variant="body1" sx={{ color: 'text.primary' }}>
            {formatDateTime(formData.start_date) || 'Not set'}
          </Typography>
        </ReadOnlyField>
      );
    }

    return (
      <DateTimePicker
        label="Start Date & Time"
        value={formData.start_date}
        onChange={handleStartDateChange}
        slotProps={{
          textField: {
            fullWidth: true,
            size: 'small',
            sx: commonTextFieldSx
          }
        }}
      />
    );
  };

  const renderEndField = () => {
    if (isReadOnly) {
      return (
        <ReadOnlyField label="End Date & Time">
          <Typography variant="body1" sx={{ color: 'text.primary' }}>
            {formatDateTime(formData.end_date) || 'Not set'}
          </Typography>
        </ReadOnlyField>
      );
    }

    return (
      <DateTimePicker
        label="End Date & Time"
        value={formData.end_date}
        onChange={handleEndDateChange}
        slotProps={{
          textField: {
            fullWidth: true,
            size: 'small',
            sx: commonTextFieldSx
          }
        }}
      />
    );
  };

  const renderNotesField = () => {
    if (isReadOnly) {
      return (
        <ReadOnlyField label="Notes" alignTop minHeight={112}>
          <Typography
            variant="body2"
            sx={{
              color: 'text.primary',
              whiteSpace: 'pre-wrap',
              width: '100%'
            }}
          >
            {formData.notes?.trim() ? formData.notes : 'No notes added'}
          </Typography>
        </ReadOnlyField>
      );
    }

    return (
      <TextField
        label="Notes"
        name="notes"
        value={formData.notes}
        onChange={handleChange}
        fullWidth
        multiline
        rows={4}
        size="small"
        placeholder="Add any additional notes..."
        sx={{
          ...commonTextFieldSx,
          '& .MuiOutlinedInput-root': {
            ...commonTextFieldSx['& .MuiOutlinedInput-root'],
            alignItems: 'flex-start'
          }
        }}
      />
    );
  };

  const renderServicesSection = () => {
    if (isReadOnly) {
      const servicesToDisplay = Array.isArray(formData.services) ? formData.services.filter(Boolean) : [];
      const hasServices = servicesToDisplay.length > 0;

      return (
        <Grid container spacing={2}>
          <Grid item xs={12} md={6} sx={halfWidthItemSx}>
            <ReadOnlyField label="Service" alignTop={hasServices}>
              {hasServices ? (
                servicesToDisplay.map((service) => {
                  const priceNumber = service?.price != null ? Number(service.price) : null;
                  const priceLabel = Number.isFinite(priceNumber) ? `${currencySymbol}${priceNumber.toFixed(2)}` : '';
                  const chipLabel = priceLabel
                    ? `${service?.service_name || 'Service'} · ${priceLabel}`
                    : service?.service_name || 'Service';

                  return (
                    <Chip
                      key={service?.id || service?.service_id || chipLabel}
                      label={chipLabel}
                      size="small"
                      variant="outlined"
                      sx={{
                        bgcolor: 'background.paper',
                        borderRadius: 2,
                        fontWeight: 500
                      }}
                    />
                  );
                })
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No services assigned
                </Typography>
              )}
            </ReadOnlyField>
          </Grid>
        </Grid>
      );
    }

    return (
      <Grid container spacing={2}>
        <Grid item xs={12} md={6} sx={halfWidthItemSx}>
          <Autocomplete
            id="category-select"
            options={categories}
            loading={categoriesLoading}
            getOptionLabel={(option) => option.name || `Category ${option.id}`}
            isOptionEqualToValue={(o, v) => (o?.category_id ?? o?.id) === (v?.category_id ?? v?.id)}
            value={categories.find(c => (c.category_id === selectedCategoryId) || (c.id === selectedCategoryId)) || null}
            onChange={(_, value) => setSelectedCategoryId(value ? (value.category_id ?? value.id) : null)}
            sx={{ width: '100%' }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Category"
              fullWidth
              size="small"
              sx={commonTextFieldSx}
            />
          )}
        />
      </Grid>
      <Grid item xs={12} md={6} sx={halfWidthItemSx}>
        <FormControl fullWidth size="small" sx={commonTextFieldSx}>
          <InputLabel id="service-label">Service</InputLabel>
          <Select
            labelId="service-label"
            name="service"
            value={selectedServiceValue || ''}
              onChange={(e) => {
                const serviceId = e.target.value;
                const selectedService = services?.find(s => s.id === serviceId);

                setFormData(prev => ({
                  ...prev,
                  services: serviceId ? [{
                    id: selectedService?.id || serviceId,
                    service_id: selectedService?.id || serviceId,
                    service_name: selectedService?.service_name || '',
                    price: selectedService?.price || 0
                  }] : []
                }));
              }}
              label="Service"
            >
              {!selectedCategoryId && (
                <MenuItem disabled>Select a category first</MenuItem>
              )}
              {selectedCategoryId && servicesLoading && (
                <MenuItem disabled>Loading services...</MenuItem>
              )}
              {selectedCategoryId && servicesError && !servicesLoading && (
                <MenuItem disabled>Failed to load services</MenuItem>
              )}
              {selectedCategoryId && !servicesLoading && !servicesError && Array.isArray(services) && services.length > 0 ? (
                services.map(service => (
                  <MenuItem key={service.id} value={service.id}>
                    {service.service_name} {service.price ? `- ${formatCurrency(service.price)}` : ''}
                  </MenuItem>
                ))
              ) : (selectedCategoryId && !servicesLoading && !servicesError) ? (
                <MenuItem disabled>No services available</MenuItem>
              ) : null}
            </Select>
          </FormControl>
        </Grid>
      </Grid>
    );
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Dialog 
        open={open} 
        onClose={onClose}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
            ...dialogPaperSx
          }
        }}
      >
        <DialogTitle sx={{ 
          borderBottom: '1px solid',
          borderColor: 'divider',
          pb: 2.5, 
          pt: 3,
          fontSize: '1.5rem',
          fontWeight: 600,
          color: 'text.primary',
          background: currentTheme?.primary ? `linear-gradient(135deg, ${currentTheme.primary}08 0%, ${currentTheme.primary}04 100%)` : 'transparent'
        }}>
          {appointmentToUse ? (canEdit ? 'Edit Appointment' : 'View Appointment') : 'New Appointment'}
        </DialogTitle>
        
        <DialogContent
          dividers
          sx={{
            pt: 3,
            pb: 3,
            px: { xs: 2, sm: 3 },
            minHeight: { xs: 'auto', md: 520 }
          }}
        >
          <Box sx={{ width: '100%' }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={8} sx={{ ...halfWidthItemSx, minWidth: { xs: '100%', md: 360 } }}>
                {renderClientField()}
              </Grid>
              <Grid item xs={12} md={4} sx={halfWidthItemSx}>
                {renderStatusField()}
              </Grid>
              <Grid item xs={12} md={6} sx={halfWidthItemSx}>
                {renderStartField()}
              </Grid>
              <Grid item xs={12} md={6} sx={halfWidthItemSx}>
                {renderEndField()}
              </Grid>
              <Grid item xs={12} md={6} sx={halfWidthItemSx}>
                {renderProviderField()}
              </Grid>
              <Grid item xs={12} md={6} sx={halfWidthItemSx}>
                {renderLocationField()}
              </Grid>
              <Grid item xs={12}>
                {renderNotesField()}
              </Grid>
              <Grid item xs={12}>
                <Divider sx={{ mt: 1, mb: 2 }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, color: 'text.primary' }}>
                  Services
                </Typography>
                {renderServicesSection()}
              </Grid>
            </Grid>
            {error && (
              <Box sx={{ mt: 2 }}>
                <Box
                  sx={{
                    color: 'error.main',
                    p: 2,
                    bgcolor: 'error.light',
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'error.main'
                  }}
                >
                  {error}
                </Box>
              </Box>
            )}
          </Box>
        </DialogContent>
        
        <DialogActions sx={{ 
          p: 3, 
          pt: 2,
          borderTop: '1px solid',
          borderColor: 'divider',
          background: currentTheme?.primary ? `linear-gradient(135deg, ${currentTheme.primary}08 0%, ${currentTheme.primary}04 100%)` : 'transparent',
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 2
        }}>
          <Button 
            onClick={onClose} 
            disabled={loading} 
            variant="outlined"
            sx={{ 
              minWidth: 100, 
              textTransform: 'none',
              borderRadius: 2
            }}
          >
            {appointmentToUse && !canEdit ? 'Close' : 'Cancel'}
          </Button>
          {(!appointmentToUse || canEdit) && (
            <Button 
              onClick={handleSubmit} 
              variant="contained" 
              disabled={loading}
              sx={{ 
                minWidth: 100, 
                textTransform: 'none',
                borderRadius: 2,
                boxShadow: 2
              }}
            >
              {loading ? 'Saving...' : 'Save'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </LocalizationProvider>
  );
};

export default AppointmentFormDialog;
